import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Shield, Clock, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { CheckoutModal, CurrencyToggle, formatPrice } from './Checkout';

const API = process.env.REACT_APP_BACKEND_URL;

const CoachingSection = () => {
  const [currency, setCurrency] = useState('INR');
  const [contactMethod, setContactMethod] = useState('instagram');
  const [contactValue, setContactValue] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [checkout, setCheckout] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rate, setRate] = useState(84);

  const BASE_PRICE_INR = 8000;

  useEffect(() => {
    axios.get(`${API}/api/exchange-rate`).then(r => setRate(r.data.rate)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const price = currency === 'INR' ? BASE_PRICE_INR : Math.round((BASE_PRICE_INR / rate) * 100) / 100;

  const contactMethods = [
    { id: 'instagram', label: 'Instagram', placeholder: '@username' },
    { id: 'phone', label: 'Phone / WhatsApp', placeholder: '+91 ...' },
    { id: 'telegram', label: 'Telegram', placeholder: '@username' },
  ];

  const canSubmit = contactValue && name && email;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setCheckout(true);
  };

  const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <section className="section-padding relative overflow-hidden" data-testid="coaching-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #00FFFF 0%, transparent 70%)' }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        >
          <motion.div className="inline-block mb-4"
            animate={{ y: [0, -5, 0] }} transition={{ duration: 3, repeat: Infinity }}
          >
            <Phone className="w-10 h-10 mx-auto" style={{ color: '#00FFFF' }} />
          </motion.div>
          <h2 className="text-section font-heading text-white mb-3">
            Book a <span style={{ color: '#00FFFF', textShadow: '0 0 20px rgba(0,255,255,0.3)' }}>Coaching Session</span>
          </h2>
          <p className="text-[#FFD1DC] text-lg max-w-xl mx-auto mb-2">
            45-minute private audio session with Acid Rani. Real talk on intimacy, relationships, and self-discovery.
          </p>

          <div className="flex items-center justify-center gap-3 mt-4">
            <span className="text-[#CCFF00] font-heading text-3xl">{formatPrice(price, currency)}</span>
            <span className="text-white/40 text-sm">/session</span>
            <CurrencyToggle currency={currency} setCurrency={setCurrency} />
          </div>
        </motion.div>

        {/* Info Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { icon: Clock, label: '45 Minutes', sub: 'Audio Call' },
            { icon: MessageCircle, label: 'We Reach You', sub: 'With available slots' },
            { icon: Shield, label: 'Safe Space', sub: 'Respectful & private' },
            { icon: Phone, label: 'Your Platform', sub: 'IG / Phone / Telegram' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="text-center p-3 rounded-xl"
                style={{ background: 'rgba(0,255,255,0.03)', border: '1px solid rgba(0,255,255,0.1)' }}>
                <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: '#00FFFF' }} />
                <p className="text-white font-heading text-xs">{item.label}</p>
                <p className="text-white/35 text-[10px]">{item.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Details Form */}
        {!submitted ? (
          <motion.div
            className="max-w-2xl mx-auto p-6 md:p-8 rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,255,255,0.15)' }}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <h3 className="font-heading text-lg text-white mb-1">Drop your details</h3>
            <p className="text-white/35 text-xs mb-5">We'll get back to you with available slots and timings</p>

            <div className="space-y-4">
              <div>
                <label className="text-white/40 text-xs font-body block mb-1">Your Name *</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  className="checkout-input" placeholder="Your name" />
              </div>

              <div>
                <label className="text-white/40 text-xs font-body block mb-1">Email *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="checkout-input" placeholder="your@email.com" />
              </div>

              {/* Contact method */}
              <div>
                <label className="text-white/40 text-xs font-body block mb-2">How should we reach you? *</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {contactMethods.map(m => (
                    <button key={m.id}
                      onClick={() => { setContactMethod(m.id); setContactValue(''); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        contactMethod === m.id
                          ? 'bg-[#00FFFF] text-[#0A0208] font-bold' : 'bg-white/5 text-white/50 border border-white/10'
                      }`}
                    >{m.label}</button>
                  ))}
                </div>
                <input type="text" value={contactValue} onChange={e => setContactValue(e.target.value)}
                  placeholder={contactMethods.find(m => m.id === contactMethod)?.placeholder}
                  className="checkout-input" data-testid="coaching-contact-input"
                />
              </div>

              <div>
                <label className="text-white/40 text-xs font-body block mb-1">Anything you'd like to discuss? (optional)</label>
                <textarea value={note} onChange={e => setNote(e.target.value)}
                  className="checkout-input resize-none" rows={3}
                  placeholder="Brief note about what you'd like to talk about..." />
              </div>

              <p className="text-white/20 text-[10px]">Your timezone: {userTZ}</p>

              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`w-full py-4 rounded-full font-heading font-bold text-sm uppercase tracking-wider transition-all ${
                  canSubmit
                    ? 'text-[#0A0208] shadow-[0_0_20px_rgba(0,255,255,0.4)]'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
                style={canSubmit ? { background: 'linear-gradient(135deg, #00FFFF, #00CED1)' } : {}}
                data-testid="coaching-book-btn"
              >
                Submit & Pay
              </button>
            </div>

            {/* Disclaimers */}
            <div className="mt-5 space-y-2">
              <div className="p-3 rounded-xl" style={{ background: 'rgba(255,209,220,0.04)', border: '1px solid rgba(255,209,220,0.1)' }}>
                <p className="text-[#FFD1DC]/60 text-[11px] leading-relaxed">
                  This is a safe and respectful coaching space. You are welcome to discuss questions around
                  intimacy, relationships, and sexuality in an educational and personal growth context.
                  Explicit or erotic conversations intended for gratification are not permitted.
                </p>
              </div>
              <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white/30 text-[10px] leading-relaxed">
                  <span className="text-white/50 font-bold">Please note:</span> Acid Rani is not a licensed therapist,
                  psychologist, or mental health professional. These sessions are personal coaching conversations rooted in
                  lived experience — not clinical therapy. If you are experiencing a mental health crisis or need
                  professional support, please reach out to a qualified therapist or helpline in your area.
                </p>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div className="max-w-md mx-auto text-center p-8 rounded-2xl"
            style={{ background: 'rgba(0,255,255,0.05)', border: '1px solid rgba(0,255,255,0.2)' }}
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          >
            <Phone className="w-10 h-10 mx-auto text-[#00FFFF] mb-3" />
            <h3 className="font-heading text-xl text-white mb-2">You're in!</h3>
            <p className="text-white/50 text-sm">We'll reach out with available slots shortly. Check your inbox.</p>
          </motion.div>
        )}
      </div>

      {checkout && (
        <CheckoutModal
          isOpen={checkout}
          onClose={() => setCheckout(false)}
          itemType="coaching"
          itemName="Coaching Session — 45 min (Acid Rani)"
          baseAmount={BASE_PRICE_INR}
          baseCurrency="INR"
          extraData={{ contact_method: contactMethod, contact_value: contactValue, extra_details: note }}
          onSuccess={() => { setCheckout(false); setSubmitted(true); }}
        />
      )}
    </section>
  );
};

export default CoachingSection;
